import { computed, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from './cookie.service';
import { AuditService } from './audit.service';
import { filter, interval, Subscription } from 'rxjs';
import { Utils } from '../utils/utils';
import { TranslocoService } from '@ngneat/transloco';
import { ConfigService } from './config.service';
import { ModalService } from './modal.service';
import { FocusService } from './focus.service';

@Injectable({
  providedIn: 'root',
})
export class SessionExpirationService {

  private translationService = inject(TranslocoService);
  private configService = inject(ConfigService);
  private focusService = inject(FocusService);


  private notifyThreshold = 2; // minutes
  private sessionExpirationCheckInterval = 30000; // milliseconds
  private timeZone = computed(() => {
    return this.configService.configState().store?.timeZone ?? 'America/New_York';
  });
  private notified: Subscription | undefined = undefined;
  private notificationModalTimestamp = '';
  private sessionExpirationTimestamp = '';

  private router = inject(Router);
  private cookieService = inject(CookieService);
  private modalService = inject(ModalService);
  private auditService = inject(AuditService);

  constructor() {}

  startChecking() {
    this.subscribeToNotification();
  }

  subscribeToNotification() {
    if (!!this.notified && !this.notified?.closed) {
      this.notified?.unsubscribe();
    }
    this.notified = interval(this.sessionExpirationCheckInterval)
      .pipe(filter(() => this.router.url.indexOf('expiration') === -1))
      .subscribe(() => {
        if (this.shouldNotifyUser()) {
          this.cacheNotificationTimestamps();
          this.notified?.unsubscribe();
          this.openExpirationModal();
        }
      });
  }

  public openExpirationModal() {
    const timestampMessage = `after having been previously notified at ${this.notificationModalTimestamp} (client time) that the session will expire at ${this.sessionExpirationTimestamp} (cookie expiration time)`;
    this.auditService.createAudit(() => 'Session expiration modal has been opened');
    this.modalService.openGenericModal({
      isAlert: true,
      modalTitle: this.translationService.translate('modals.session_expiration.expiration_modal.title'),
      modalMessage: this.translationService.translate('modals.session_expiration.expiration_modal.message'),
      confirmButtonText: this.translationService.translate('modals.session_expiration.expiration_modal.submit'),
      primaryAction:() => {
        return 'submitted';
      }
    })?.subscribe({
      next: (value) => {
        this.subscribeToNotification();
        if(value){
          this.auditService.createAudit(() => `User clicked on 'Update my session' button, ${timestampMessage}`);
          this.focusTopOfPage();
        }else {
          this.auditService.createAudit(() => `User dismissed session expiration notification modal ${timestampMessage}`);
          this.focusTopOfPage();
        }
      },
      error: (error) => {
        this.auditService.createAudit(() => ({
          message: `Session expiration modal has been dismissed for unknown reason with error (see details)`,
          details: error,
        }));
        this.subscribeToNotification();
      }
    });
  }

  private shouldNotifyUser(): boolean {
    const remainingMinutes = Math.round(this.getRemainingMinutes());
    if (remainingMinutes < 0) {
      return false;
    }

    return remainingMinutes <= this.notifyThreshold;
  }

  private getRemainingMinutes(): number {
    return (this.getSessionExpiration().getTime() - this.getTimeInServerTZ().getTime())/(1000*60);
  }

  private getSessionExpiration(): Date {
    const sessionExpirationStr = this.cookieService.getCookie('sessionExpiry');
    return Utils.parseIsoDate(sessionExpirationStr, this.timeZone()) || this.getTimeInServerTZ();
  }

  private getTimeInServerTZ(): Date {
    return Utils.getTzDate(new Date(), this.timeZone());
  }

  private cacheNotificationTimestamps() {
    this.notificationModalTimestamp = new Date().toISOString();
    this.sessionExpirationTimestamp = this.cookieService.getCookie('sessionExpiry');
  }

  private focusTopOfPage(): void {
    setTimeout(() => {
      const menuButton = document.querySelector<HTMLElement>('.menu-toggle-button');
      const skipLink = document.querySelector<HTMLElement>('.skip-to-content');
      const target = [menuButton, skipLink].find((element): element is HTMLElement => this.isFocusableTarget(element));
      if (target) {
        this.focusService.focus(target);
      }
    }, 300);
  }

  private isFocusableTarget(element: HTMLElement | null): element is HTMLElement {
    return !!element && element.getClientRects().length > 0;
  }
}